// src/context/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from "react";
import { API_BASE_URL, BACKEND_URL } from "../config/apiKey.js";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("auth_token") || null);

  // Check if user is authenticated on mount
  useEffect(() => {
    // 🚪 Handle window close without logout
    const handleBeforeUnload = (e) => {
      // If user is logged in, show a warning
      if (token && user) {
        const message = "Please remember to logout before closing the window for security.";
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    const checkAuth = async () => {
      const storedToken = localStorage.getItem("auth_token");
      if (storedToken) {
        try {
          let response = await fetch(`${API_BASE_URL}/api/auth/me`, {
            headers: {
              Authorization: `Bearer ${storedToken}`,
              "X-Requested-With": "XMLHttpRequest",
            },
          });
          
          // Check if response is HTML (routing issue)
          let responseText = "";
          let contentType = response.headers.get("content-type") || "";
          let isHtml = contentType.includes("text/html");

          if (isHtml || !response.ok) {
             // If misrouted or failed, try fallback
             if (isHtml && API_BASE_URL !== BACKEND_URL) {
                console.warn("⚠️ Auth check misrouted. Trying fallback...");
                response = await fetch(`${BACKEND_URL}/api/auth/me`, {
                  headers: {
                    Authorization: `Bearer ${storedToken}`,
                    "X-Requested-With": "XMLHttpRequest",
                  },
                });
                contentType = response.headers.get("content-type") || "";
             }
          }

          if (contentType && contentType.includes("application/json")) {
            if (response.ok) {
              const userData = await response.json();
              setUser(userData);
              setToken(storedToken);
            } else {
              console.warn(`Auth check failed (Status: ${response.status}). Removing token.`);
              localStorage.removeItem("auth_token");
              setToken(null);
            }
          } else {
            // Server returned HTML instead of JSON - API not available
            const text = await response.text();
            console.error("Auth check: Server returned HTML instead of JSON. API may not be running or is misconfigured.");
            console.debug("Response text snippet:", text.substring(0, 200));
            
            // Don't remove token if it's likely a temporary server/routing error
            if (response.status === 401 || response.status === 403) {
              localStorage.removeItem("auth_token");
              setToken(null);
            }
          }
        } catch (error) {
          console.error("Auth check failed:", error);
          localStorage.removeItem("auth_token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [token, user]);

  const login = async (email, password) => {
    try {
      let response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        mode: "cors",
        body: JSON.stringify({ email, password }),
      });

    // Check for common error status codes first
    if (response.status === 403 || response.status === 401 || response.status === 400 || response.status === 404 || response.status === 500 || response.status === 503) {
      try {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await response.json();
          
          if (response.status === 403 && data.error === "ALREADY_LOGGED_IN") {
            return { 
              success: false, 
              error: "ALREADY_LOGGED_IN", 
              message: data.message || "You are already logged in. Please logout first to continue.",
              needsLogout: true
            };
          }

          if (response.status === 403 && data.error === "CONCURRENT_LOGIN_LIMIT_REACHED") {
            return { 
              success: false, 
              error: "CONCURRENT_LOGIN_LIMIT_REACHED", 
              message: data.message,
              maxLogins: data.maxLogins,
              currentLogins: data.currentLogins
            };
          }

          if (response.status === 403 && data.error === "ACCOUNT_LOCKED") {
            return { 
              success: false, 
              error: "ACCOUNT_LOCKED", 
              message: data.message || "your account has been blocked kindly logout and contact admin" 
            };
          }
          
          if (response.status === 401) {
            return { success: false, error: "UNAUTHORIZED", message: data.message || "Invalid credentials" };
          }

          if (data.error === "PASSWORD_RESET_REQUIRED") {
            return { success: false, error: "PASSWORD_RESET_REQUIRED", message: data.message };
          }

          return { 
            success: false, 
            error: data.error || `ERROR_${response.status}`, 
            message: data.message || "An error occurred",
            needsLogout: data.needsLogout || false,
            maxLogins: data.maxLogins,
            currentLogins: data.currentLogins
          };
        }
      } catch (e) {
        console.error(`Error parsing ${response.status} JSON response:`, e);
      }
    }

      // Check if response is actually JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("Non-JSON response received from login:", text.substring(0, 500));
        console.error("DEBUG INFO:", {
          API_BASE_URL,
          status: response.status,
          contentType,
          url: `${API_BASE_URL}/api/auth/login`
        });
        
        const isHtml = text.includes("<!doctype html>") || text.includes("<html");
      
      if (isHtml) {
        // Diagnostic: If we got HTML, and it's a 200, it's definitely misrouted
        if (response.status === 200) {
          return { 
            success: false, 
            error: "MISROUTED_HTML_RESPONSE",
            message: `The server returned a page instead of data. 
            API_BASE_URL: ${API_BASE_URL}
            
            This usually means the API_BASE_URL is pointing to the frontend instead of the backend API.
            Current origin: ${window.location.origin}`,
            needsLogout: true
          };
        }
        
        return { 
          success: false, 
          error: "SERVER_HTML_ERROR",
          message: `Server returned an error page (Status: ${response.status}). The service might be temporarily down.`
        };
      }

      return { 
        success: false, 
        error: "API_ERROR",
        message: `API request failed (Status: ${response.status}). Please try again later.` 
      };
    }

    const data = await response.json();
      if (response.ok) {
        // Clear previous user's chat data before setting new token
        // This ensures chats are separated per user
        localStorage.removeItem("exivis_chats");
        localStorage.removeItem("exivis_currentChat");
        localStorage.removeItem("exivis_messages");
        
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem("auth_token", data.token);
        return { success: true };
      } else {
        // If we reach here, it means it's an error status (not 2xx) 
        // but it wasn't handled in the early exit block for JSON errors
        return { success: false, error: data.error || "Login failed", message: data.message };
      }
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, error: error.message || "Network error. Please check if the API is accessible." };
    }
  };

  const signup = async (name, email, password) => {
    try {
      let response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        mode: "cors",
        body: JSON.stringify({ name, email, password }),
      });

      // Check if response is actually JSON
      let contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        // Try fallback to BACKEND_URL if misrouted
        if (API_BASE_URL !== BACKEND_URL) {
          console.warn("⚠️ Register request misrouted to frontend. Trying fallback to BACKEND_URL...");
          response = await fetch(`${BACKEND_URL}/api/auth/register`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({ name, email, password }),
          });
          contentType = response.headers.get("content-type");
        }
      }

      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("Non-JSON response received from register:", text.substring(0, 500));
        console.error("DEBUG INFO:", {
          API_BASE_URL,
          status: response.status,
          contentType,
          url: `${API_BASE_URL}/api/auth/register`
        });
        
        const isHtml = text.includes("<!doctype html>") || text.includes("<html");
        
        if (response.status === 200 && isHtml) {
          return { 
            success: false, 
            error: "SERVER_CONFIGURATION_ERROR",
            message: `The server returned HTML instead of JSON. This usually means the backend is not reachable at ${API_BASE_URL}` 
          };
        }

        return { 
          success: false, 
          error: `API request failed (Status: ${response.status}). The server returned ${contentType || 'unknown content'} instead of JSON.` 
        };
      }

      const data = await response.json();
      if (response.ok) {
        // Don't auto-login after signup - user should log in manually
        // setToken(data.token);
        // setUser(data.user);
        // localStorage.setItem("auth_token", data.token);
        return { success: true };
      } else {
        return { success: false, error: data.error || "Signup failed" };
      }
    } catch (error) {
      console.error("Signup error:", error);
      return { success: false, error: error.message || "Network error. Please check if the API is accessible." };
    }
  };

  const loginWithGoogle = () => {
    // Redirect to backend Google OAuth endpoint
    window.location.href = `${API_BASE_URL}/api/auth/google`;
  };

  const handleOAuthCallback = async (newToken) => {
    setLoading(true);
    try {
      localStorage.setItem("auth_token", newToken);
      setToken(newToken);
      
      console.log("🔵 handleOAuthCallback: Token set, verifying...");

      // Clear previous user's chat data
      localStorage.removeItem("exivis_chats");
      localStorage.removeItem("exivis_currentChat");
      localStorage.removeItem("exivis_messages");

      // Verify token and get user data
      let response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${newToken}`,
          "Accept": "application/json"
        },
        mode: "cors",
      });

      // Check if response is actually JSON (detect misrouting)
      let contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        // Try fallback to BACKEND_URL if misrouted
        if (API_BASE_URL !== BACKEND_URL) {
          console.warn("⚠️ OAuth verification misrouted to frontend. Trying fallback to BACKEND_URL...");
          response = await fetch(`${BACKEND_URL}/api/auth/me`, {
            headers: {
              Authorization: `Bearer ${newToken}`,
              "X-Requested-With": "XMLHttpRequest",
            },
          });
          contentType = response.headers.get("content-type");
        }
      }

      if (contentType && contentType.includes("application/json") && response.ok) {
        const userData = await response.json();
        console.log("✅ handleOAuthCallback: User verified:", userData.email);
        setUser(userData);
        return { success: true };
      } else {
        const contentType = response.headers.get("content-type");
        const responseText = await response.text();
        console.error("❌ handleOAuthCallback: Verification failed", response.status);
        
        let errorMsg = "Verification failed";
        try {
          if (contentType && contentType.includes("application/json")) {
            const errorData = JSON.parse(responseText);
            errorMsg = errorData.error || errorMsg;
          }
        } catch (e) {
          console.error("Error parsing error response:", e);
        }
        
        localStorage.removeItem("auth_token");
        setToken(null);
        setUser(null);
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("OAuth callback error:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      if (token) {
        // Use a flag to avoid recursive calls if something goes wrong
        const currentToken = token;
        
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${currentToken}`,
            "Accept": "application/json"
          },
        });
      }
    } catch (e) {
      // Ignore logout errors, just clear local state
      console.warn("Logout request failed:", e);
    } finally {
      // CLEAR EVERYTHING - Be aggressive to prevent session leakage
      localStorage.removeItem("auth_token");
      localStorage.removeItem("exivis_chats");
      localStorage.removeItem("exivis_currentChat");
      localStorage.removeItem("exivis_messages");
      localStorage.removeItem("exivis_projects");
      localStorage.removeItem("exivis_currentProject");
      
      // Clear cookies if any were set (optional but good practice)
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });

      setUser(null);
      setToken(null);
      
      // Explicitly set loading to false to unblock any UI waiting on auth state
      setLoading(false);
      
      console.log("🔒 Session cleared successfully.");
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    signup,
    loginWithGoogle,
    handleOAuthCallback,
    logout,
    isAuthenticated: !!token && !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};


